import React from 'react';
import { MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { ConditionValueValue } from 'components/campaigns/models/CampaignItem';
import {
    isTimeOfDay,
    monthsOfYear,
    TimeConditionKey,
    timeOfDayLabel
} from 'components/campaigns/models/CampaignTriggerCondition';
import { DAYS_OF_THE_WEEK } from 'components/location/models/LocationModel';
import { titleize } from 'lib/helpers';
import { isArray, isArrayOf, isString } from 'lib/typeguards';
import { Option } from 'lib/types';

interface TimeBasedConditionsProps<T extends TimeConditionKey> {
    type: T;
    value: ConditionValueValue;
    onChange: (value: ConditionValueValue) => void;
}

const timeOfDayOptions = Object.entries(timeOfDayLabel).map(([value, label]) => ({ value, label }));
const dayOfWeekOptions = DAYS_OF_THE_WEEK.map((label, index) => ({
    label: titleize(label),
    value: String(index)
}));
const monthOfYearOptions = monthsOfYear.map((label, index) => ({ label, value: String(index) }));
const variantToOptions: Record<TimeConditionKey, Option[]> = {
    [TimeConditionKey.dayOfWeek]: dayOfWeekOptions,
    [TimeConditionKey.timeOfDay]: timeOfDayOptions,
    [TimeConditionKey.weekOfYear]: [],
    [TimeConditionKey.monthOfYear]: monthOfYearOptions
};

export function TimeBasedConditions<T extends TimeConditionKey>(props: TimeBasedConditionsProps<T>) {
    const { onChange, value } = props;
    const handleSelectChange = React.useCallback(
        (e: SelectChangeEvent<string | string[]>) => {
            onChange(e.target.value);
        },
        [onChange]
    );
    const handleNumberChange = React.useCallback(
        (e: React.ChangeEvent<{ value: any }>) => {
            if (isString(e.target.value)) {
                onChange(Number(e.target.value));
            }
            if (isArray(e.target.value) && isArrayOf(isTimeOfDay, e.target.value)) {
                onChange(e.target.value);
            }
        },
        [onChange]
    );
    switch (props.type) {
        case TimeConditionKey.timeOfDay: {
            const formattedValue = Array.isArray(value) ? value : [];
            return (
                <Select
                    fullWidth
                    value={formattedValue}
                    onChange={handleSelectChange}
                    placeholder="Choose a trigger"
                    multiple
                >
                    {variantToOptions[props.type].map(({ value, label }) => (
                        <MenuItem key={`select-condition-value-${value}`} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            );
        }
        case TimeConditionKey.dayOfWeek:
        case TimeConditionKey.monthOfYear:
            return (
                <Select
                    fullWidth
                    value={value ? String(value) : ''}
                    onChange={handleSelectChange}
                    placeholder="Choose a trigger"
                >
                    {variantToOptions[props.type].map(({ value: currentValue, label }) => (
                        <MenuItem key={`select-condition-value-${currentValue}`} value={currentValue}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            );
        case TimeConditionKey.weekOfYear:
            return (
                <TextField
                    value={value || ''}
                    type="number"
                    inputProps={{ min: 0 }}
                    onChange={handleNumberChange}
                />
            );
        default:
            return null;
    }
}
